import { useEffect, useState } from "react";

import { useCreateAlert } from "@/api/createAlert";
import { useAlertData } from "@/api/getAlert";
import { useSettings } from "@/api/getSettings";
import { type UpdateAlertParams, useUpdateAlert } from "@/api/updateAlert";
import { useUpdateSettings } from "@/api/updateSetting";
import { Card, CardContent } from "../ui/card";
import { Switch } from "../ui/switch";

function isValidUpdateAlertParams(
	params: unknown,
): params is UpdateAlertParams {
	return typeof params === "object" && params !== null && "alert_id" in params;
}

const NotificationSettings = () => {
	const { data: settings, isLoading: isSettingsLoading } = useSettings();
	const { data: alertData, isLoading: isAlertLoading } = useAlertData({
		energy: "ELEC",
	});

	const { mutate: createAlert, isPending: isCreatingAlert } = useCreateAlert();

	const { mutate: updateAlert, isPending: isUpdatingAlert } = useUpdateAlert();
	const { mutate: updateSettings, isPending: isUpdating } = useUpdateSettings();

	const [localSettings, setLocalSettings] = useState({
		email_enabled: false,
		sms_enabled: false,
		push_enabled: false,
	});

	const [localAlerts, setLocalAlerts] = useState({
		usageLimitActive: false,
		dataAvailableActive: false,
	});

	useEffect(() => {
		if (settings) {
			setLocalSettings({
				email_enabled: settings.email_enabled,
				sms_enabled: settings.sms_enabled,
				push_enabled: settings.push_enabled,
			});
		}
	}, [settings]);

	useEffect(() => {
		if (alertData?.alerts_details) {
			const usageLimitAlert = alertData.alerts_details.find(
				(alert) =>
					alert.alert_type === "USAGE_LIMIT_CONSUMPTION_MONTHLY" ||
					alert.alert_type === "USAGE_LIMIT_COST_MONTHLY",
			);
			const dataAvailableAlert = alertData.alerts_details.find(
				(alert) => alert.alert_type === "DATA_AVAILABLE",
			);

			setLocalAlerts({
				usageLimitActive: usageLimitAlert?.is_active ?? false,
				dataAvailableActive: dataAvailableAlert?.is_active ?? false,
			});

			// Create DATA_AVAILABLE alert if it does not exist
			if (!dataAvailableAlert) {
				createAlert({
					energy: "ELEC",
					alert_type: "DATA_AVAILABLE",
				});
			}
		}
	}, [alertData, createAlert]);

	const handleSettingChange = (
		key: "email_enabled" | "sms_enabled" | "push_enabled",
	) => {
		const newSettings = {
			...localSettings,
			[key]: !localSettings[key],
		};
		setLocalSettings(newSettings);
		updateSettings(newSettings);
	};

	const handleAlertChange = (
		alertType:
			| "USAGE_LIMIT_CONSUMPTION_MONTHLY"
			| "USAGE_LIMIT_COST_MONTHLY"
			| "DATA_AVAILABLE",
	) => {
		const alert = alertData?.alerts_details.find(
			(a) => a.alert_type === alertType,
		);
		if (!alert) return;

		const newIsActive = !alert.is_active;
		const isUsageLimit =
			alertType === "USAGE_LIMIT_CONSUMPTION_MONTHLY" ||
			alertType === "USAGE_LIMIT_COST_MONTHLY";

		setLocalAlerts((prev) => ({
			...prev,
			[isUsageLimit ? "usageLimitActive" : "dataAvailableActive"]: newIsActive,
		}));

		const updateAlertParams = {
			alert_id: alert.alert_id,
			energy: alert.energy,
			alert_type: alertType,
			is_active: newIsActive,
			...(alertType === "USAGE_LIMIT_CONSUMPTION_MONTHLY"
				? { value: alert.value, unit: alert.unit as "kWh" }
				: alertType === "USAGE_LIMIT_COST_MONTHLY"
					? { value: alert.value, unit: alert.unit as "EUR" }
					: {}),
		};

		if (isValidUpdateAlertParams(updateAlertParams)) {
			updateAlert(updateAlertParams, {
				onSuccess: () => {
					console.log("Alert updated successfully");
				},
				onError: (error) => {
					console.error("Error updating alert:", error);
					setLocalAlerts((prev) => ({
						...prev,
						[isUsageLimit ? "usageLimitActive" : "dataAvailableActive"]:
							!newIsActive,
					}));
				},
			});
		}
	};

	if (isSettingsLoading || isAlertLoading || isCreatingAlert) {
		return (
			<div className="w-full">
				<Card className="mb-4 py-4">
					<CardContent className="px-6 pb-6">
						<div className="animate-pulse space-y-4">
							<div className="h-6 bg-gray-200 rounded w-1/4" />
							<div className="space-y-4">
								<div className="h-4 bg-gray-200 rounded w-3/4" />
								<div className="h-4 bg-gray-200 rounded w-3/4" />
							</div>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="w-full">
			<Card className="mb-4 py-4">
				<CardContent className="px-6 pb-6">
					<h2 className="font-bold text-xl mb-6">Paramètres de notification</h2>

					<section className="mb-6">
						<h3 className="text-gray-500 text-sm font-medium mb-3">Contenus</h3>
						<div className="space-y-8">
							<div className="flex justify-between items-center">
								<div>
									<p className="font-medium">Alertes de dépassement</p>
								</div>
								<Switch
									checked={localAlerts.usageLimitActive}
									onCheckedChange={() => {
										const alert = alertData?.alerts_details.find(
											(a) =>
												a.alert_type === "USAGE_LIMIT_CONSUMPTION_MONTHLY" ||
												a.alert_type === "USAGE_LIMIT_COST_MONTHLY",
										);
										if (alert) {
											handleAlertChange(
												alert.alert_type as
													| "USAGE_LIMIT_CONSUMPTION_MONTHLY"
													| "USAGE_LIMIT_COST_MONTHLY",
											);
										}
									}}
									disabled={isUpdatingAlert}
								/>
							</div>

							<div className="flex justify-between items-center">
								<div>
									<p className="font-medium">
										Disponibilité données mensuelles
									</p>
								</div>
								<Switch
									checked={localAlerts.dataAvailableActive}
									onCheckedChange={() => handleAlertChange("DATA_AVAILABLE")}
									disabled={isUpdatingAlert}
								/>
							</div>
						</div>
					</section>

					<section className="space-y-4">
						<h3 className="text-gray-500 text-sm font-medium mb-3">
							Canaux de communication
						</h3>
						<div className="flex justify-between items-center">
							<div>
								<p className="font-medium">Via e-mail</p>
							</div>
							<Switch
								checked={localSettings.email_enabled}
								onCheckedChange={() => handleSettingChange("email_enabled")}
								disabled={isUpdating}
							/>
						</div>
						<div className="flex justify-between items-center">
							<div>
								<p className="font-medium">
									Via notifications push sur votre téléphone
								</p>
							</div>
							<Switch
								checked={localSettings.push_enabled}
								onCheckedChange={() => handleSettingChange("push_enabled")}
								disabled={isUpdating}
							/>
						</div>
					</section>
				</CardContent>
			</Card>
		</div>
	);
};

export default NotificationSettings;
